import {ParamComponent} from 'panel';
import produce from 'immer';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {TemplateSpecs, TemplateType} from 'common/report/dashboards/types';

import css from './index.inline.styl';
import {template} from './template';
import {
  ADVANCED_TEMPLATE_TYPES_TO_TUTORIAL_MAP,
  RHSPanelParams,
  RHSPanelProps,
  RHSPanelState,
  TemplateField,
  TutorialConfig,
} from './types';

export class RHSPanel extends ParamComponent<RHSPanelParams, RHSPanelState, IronContextRegistry> {
  // START: copied from boards-template-modal
  readonly TEMPLATE_SELECTION_STEP = 0;
  readonly EVENT_SELECTION_STEP = 1;
  readonly WHAT_TO_TRACK_URL = `https://developer.mixpanel.com/docs/plan-your-implementation#what-to-track`;
  readonly VALUE_MOMENT_FIELD_TOOLTIP_TEXT = `For a social media platform, it could be creating a post; for a streaming service, it's watching a video; for e-commerce, it might be completing a purchase. [Learn More](https://developer.mixpanel.com/docs/plan-your-implementation#what-to-track)`;

  hasRequiredFields = (requiredFields: TemplateField[]): boolean => {
    return requiredFields.every((field) => !!this.state.fields[field]);
  };

  readonly TEMPLATE_CONFIGS = Object.keys(TemplateSpecs).reduce((acc, key) => {
    const {requiredFields, ...rest} = TemplateSpecs[key];
    return {
      ...acc,
      [key]: {
        ...rest,
        requiredFieldsCheck: () => this.hasRequiredFields(requiredFields),
      },
    };
  }, {});

  elRefs = {};

  setElRef(ref, el) {
    this.elRefs[ref] = el;
  }
  // END: copied from boards-template-modal
  get config() {
    return {
      contexts: [`currentProject` as const],
      css,
      defaultState: {
        fields: {},
        calendarInputOpen: false,
        tutorialCurrentStepIndex: 0,
        showTutorial: false,
        hideTutorial: false,
      },
      helpers: {
        getNoDataEventString: (): string => {
          switch (this.params.selectedTemplate) {
            case TemplateType.KPI:
            case TemplateType.FEATURE_LAUNCH:
              return `just two events`;
            case TemplateType.LIFECYCLE_COHORT_ANALYSIS:
              return `just one event`;
            default:
              return `some events`;
          }
        },
        hasIntegratedArb: (): boolean | undefined => this.getContext(`currentProject`).get()?.value?.hasIntegratedArb,
        updateField: (value: any, eventName: TemplateField): void => {
          this.update(
            produce(this.state, (draft) => {
              draft.fields[eventName] = value;
            }),
          );
          this.helpers.setTutorialStepIndex();
        },
        removeField: (eventName: TemplateField): void => {
          this.update(
            produce(this.state, (draft) => {
              delete draft.fields[eventName];
            }),
          );
        },
        shouldDisableFinishButton: (): boolean | undefined => {
          return !this.TEMPLATE_CONFIGS[this.params.selectedTemplate]?.requiredFieldsCheck();
        },
        createTemplateBoard: () => {
          if (this.helpers.shouldDisableFinishButton()) {
            return;
          }
          //TODO(RHS): create new board with blueprint and events
        },
        startTutorial: (): void => {
          this.update({showTutorial: true});
        },
        dismissTutorial: (): void => {
          this.update({hideTutorial: true});
        },
        getTemplateTutorialConfig: (): TutorialConfig => {
          return ADVANCED_TEMPLATE_TYPES_TO_TUTORIAL_MAP.get(this.params?.selectedTemplate);
        },
        setTutorialStepIndex: (): void => {
          const tutorial = this.helpers.getTemplateTutorialConfig();
          const {tutorialCurrentStepIndex} = this.state;
          if (tutorial) {
            let stepIndex = tutorialCurrentStepIndex;
            let [templateField, templateStep] = Object.entries(tutorial)[stepIndex];
            while (this.state.fields[templateField]) {
              // check each tutorial entry to see if it's field wasn't already filled out
              stepIndex = templateStep.nextStepIndex;
              [templateField, templateStep] = Object.entries(tutorial)[stepIndex];
            }
            this.update({tutorialCurrentStepIndex: stepIndex});
          }
        },
      },
      useShadowDom: true,
      params: {
        mode: String,
        selectedTemplate: String,
      },
      template,
    };
  }
}

export const RHSPanelEl = defineAndMakeJsxFactory<RHSPanelProps>(`mp-rhs-panel`, RHSPanel);
export const RHSPanelReact = panel2react<RHSPanelProps>(`mp-rhs-panel`, RHSPanel);
